import { useState, useEffect } from "react";
import { useMFA, useMFARequired, useMFASessionVerification } from "@/hooks/useMFA";
import { MFAEnrollment } from "./MFAEnrollment";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Shield,
  Loader2,
  AlertTriangle,
  Key,
  LogOut,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface AdminMFAGateProps {
  children: React.ReactNode;
}

export const AdminMFAGate = ({ children }: AdminMFAGateProps) => {
  const mfaRequired = useMFARequired();
  const { isEnrolled, isVerified, isLoading, error, verifyMFASession, clearError } = useMFA();
  const { isSessionVerified, markSessionVerified } = useMFASessionVerification();

  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  // If MFA is not required (non-admin), bypass the gate
  if (!mfaRequired) {
    return <>{children}</>;
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-hero">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Checking security status...</p>
        </div>
      </div>
    );
  }

  // Not enrolled - show enrollment flow
  if (!isEnrolled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-hero p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <Shield className="w-12 h-12 text-primary mx-auto mb-2" />
            <h1 className="text-2xl font-bold">Admin Security Setup</h1>
            <p className="text-muted-foreground">
              Two-factor authentication is required for admin access
            </p>
          </div>
          <MFAEnrollment />
        </div>
      </div>
    );
  }

  // Enrolled but not verified for this session - show verification prompt
  if (isEnrolled && !isSessionVerified) {
    const handleVerify = async () => {
      if (verificationCode.length !== 6) return;

      setIsVerifying(true);
      setVerifyError(null);

      const success = await verifyMFASession(verificationCode);

      if (success) {
        markSessionVerified();
      } else {
        setVerifyError("Invalid verification code. Please try again.");
      }

      setIsVerifying(false);
      setVerificationCode('');
    };

    const handleSignOut = async () => {
      await supabase.auth.signOut();
      window.location.href = '/';
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-hero p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-2">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Key className="w-6 h-6 text-primary" />
              </div>
            </div>
            <CardTitle>Verify Your Identity</CardTitle>
            <CardDescription>
              Enter the 6-digit code from your authenticator app
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {(verifyError || error) && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Verification Failed</AlertTitle>
                <AlertDescription>{verifyError || error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="mfa-code">Authentication Code</Label>
              <Input
                id="mfa-code"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                className="text-center text-2xl tracking-[0.5em] font-mono"
                autoComplete="one-time-code"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && verificationCode.length === 6) {
                    handleVerify();
                  }
                }}
              />
            </div>

            <Button
              onClick={handleVerify}
              disabled={isVerifying || verificationCode.length !== 6}
              className="w-full"
            >
              {isVerifying ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Shield className="w-4 h-4 mr-2" />
              )}
              Verify & Continue
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or</span>
              </div>
            </div>

            <Button
              variant="ghost"
              onClick={handleSignOut}
              className="w-full text-muted-foreground"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              Having trouble? Contact a super admin to reset your MFA.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // All checks passed - render children
  return <>{children}</>;
};
